import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, CheckCircle, FileVideo, X, XCircle } from "lucide-react";

import {
  useLazyGetVideoAnalysisStatusQuery,
  useUploadVideoForAnalysisMutation,
  type GetVideoAnalysisStatusResponse,
  type AnalysisResultJson,
  type AnalysisRep,
} from "@/store/videoAnalysisApi";

import styles from "./VideoAnalysisModal.module.scss";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface VideoAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleId: number;
  exerciseSampleId: number;
  exerciseType?: string;
}

type ModalView = "upload" | "processing" | "result" | "error";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ACCEPTED_TYPES = ["video/mp4", "video/quicktime", "video/x-msvideo"];
const MAX_DURATION_SECONDS = 30;
const POLL_INTERVAL_MS = 5000;

const validateVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => reject(new Error("Не удалось прочитать видео"));
    video.src = URL.createObjectURL(file);
  });
};

const formatDuration = (seconds: number): string => {
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m} мин ${rem} сек` : `${rem} сек`;
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

// ─── Result JSON renderer ─────────────────────────────────────────────────────

interface TimelineRepProps {
  rep: AnalysisRep;
  isLast: boolean;
}

const TimelineRep = ({ rep, isLast }: TimelineRepProps): JSX.Element => {
  const isGood = rep.classification === "good";
  return (
    <div className={styles.timelineItem}>
      <div className={styles.timelineLeft}>
        <div className={`${styles.timelineDot} ${isGood ? styles.timelineDotGood : styles.timelineDotBad}`} />
        {!isLast && <div className={styles.timelineLine} />}
      </div>
      <div className={styles.timelineContent}>
        <div className={styles.timelineHeader}>
          <span className={styles.timelineRepNum}>Повтор {rep.rep}</span>
          <span className={`${styles.timelineBadge} ${isGood ? styles.timelineBadgeGood : styles.timelineBadgeBad}`}>
            {isGood ? "Хорошо" : "Ошибки"}
          </span>
        </div>
        {rep.errors.length > 0 && (
          <div className={styles.timelineErrors}>
            {rep.errors.map((err, i) => (
              <div key={i} className={styles.timelineError}>
                <span>✕</span>
                <span>{err}</span>
              </div>
            ))}
          </div>
        )}
        <div className={styles.timelineAngles}>
          <span className={styles.timelineAngle}>Бедро {rep.angles.hip.toFixed(1)}°</span>
          <span className={styles.timelineAngle}>Колено {rep.angles.knee.toFixed(1)}°</span>
          <span className={styles.timelineAngle}>Голень {rep.angles.ankle.toFixed(1)}°</span>
        </div>
      </div>
    </div>
  );
};

const TimelineView = ({ reps }: { reps: AnalysisRep[] }): JSX.Element => (
  <div className={styles.timeline}>
    {reps.map((rep, index) => (
      <TimelineRep key={rep.rep} rep={rep} isLast={index === reps.length - 1} />
    ))}
  </div>
);

const ResultJsonView = ({ data }: { data: AnalysisResultJson }): JSX.Element => {
  const isGood = data.classification === "good";
  const goodReps = data.reps.filter((r) => r.classification === "good").length;

  const allErrors = Array.from(new Set(data.reps.flatMap((r) => r.errors)));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

      {/* Общая оценка */}
      <div className={`${styles.overallBlock} ${isGood ? styles.overallGood : styles.overallBad}`}>
        <div className={styles.overallLeft}>
          <p className={styles.overallLabel}>Общая оценка</p>
          <p className={styles.overallValue}>{isGood ? "Хорошая техника" : "Требует улучшения"}</p>
        </div>
        <div className={styles.overallStats}>
          <div className={styles.overallStat}>
            <span className={styles.overallStatNum}>{data.total_reps}</span>
            <span className={styles.overallStatLabel}>повторов</span>
          </div>
          <div className={styles.overallDivider} />
          <div className={styles.overallStat}>
            <span className={styles.overallStatNum}>{goodReps}</span>
            <span className={styles.overallStatLabel}>без ошибок</span>
          </div>
        </div>
      </div>

      {/* Выявленные ошибки */}
      {allErrors.length > 0 && (
        <div className={styles.mistakesList}>
          <p className={styles.mistakesTitle}>Выявленные ошибки</p>
          {allErrors.map((err, i) => (
            <div key={i} className={styles.mistakeItem}>{err}</div>
          ))}
        </div>
      )}

      {/* Таймлайн повторов */}
      <div>
        <p className={styles.timelineTitle}>Разбор по повторам</p>
        <TimelineView reps={data.reps} />
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const VideoAnalysisModal = ({
  isOpen,
  onClose,
  scheduleId,
  exerciseSampleId,
  exerciseType,
}: VideoAnalysisModalProps): JSX.Element | null => {
  const [view, setView] = useState<ModalView>("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileDuration, setFileDuration] = useState<number | null>(null);
  const [durationError, setDurationError] = useState<string | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<GetVideoAnalysisStatusResponse | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [uploadVideo, { isLoading: isUploading }] = useUploadVideoForAnalysisMutation();
  const [fetchStatus] = useLazyGetVideoAnalysisStatusQuery();

  // ─── Polling helpers ───────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const checkStatus = useCallback(async () => {
    try {
      const result = await fetchStatus({ schedule_id: scheduleId, exercise_sample_id: exerciseSampleId }).unwrap();

      if (result.status === "done") {
        stopPolling();
        setAnalysisResult(result);
        setView("result");
      } else if (result.status === "error") {
        stopPolling();
        setView("error");
      }
      // status === 'processing' — продолжаем поллить
    } catch {
      // Ошибка сети — не останавливаем поллинг, попробуем позже
    }
  }, [fetchStatus, scheduleId, exerciseSampleId, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    checkStatus(); // сразу проверяем
    pollTimerRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);
  }, [checkStatus, stopPolling]);

  // ─── On open: проверяем уже существующий статус ────────────────────────────

  useEffect(() => {
    if (!isOpen) return;

    const checkExisting = async () => {
      try {
        const result = await fetchStatus({ schedule_id: scheduleId, exercise_sample_id: exerciseSampleId }).unwrap();

        if (result.status === "processing") {
          setView("processing");
          startPolling();
        } else if (result.status === "done") {
          setAnalysisResult(result);
          setView("result");
        } else if (result.status === "error") {
          setView("error");
        }
        // null — показываем форму загрузки (дефолт)
      } catch {
        // API вернул ошибку — скорее всего записи нет, показываем upload
      }
    };

    setView("upload");
    setSelectedFile(null);
    setFileDuration(null);
    setDurationError(null);
    checkExisting();

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, scheduleId, exerciseSampleId]);

  // ─── File handling ─────────────────────────────────────────────────────────

  const handleFile = useCallback(async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setDurationError("Поддерживаемые форматы: MP4, MOV, AVI");
      return;
    }

    setDurationError(null);
    setValidationLoading(true);
    setSelectedFile(file);

    try {
      const duration = await validateVideoDuration(file);
      setFileDuration(duration);
      if (duration > MAX_DURATION_SECONDS) {
        setDurationError(`Видео слишком длинное (${formatDuration(duration)}). Максимум 30 секунд.`);
      }
    } catch {
      setDurationError("Не удалось прочитать видео. Проверьте формат файла.");
    } finally {
      setValidationLoading(false);
    }
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // сбрасываем input, чтобы можно было повторно выбрать тот же файл
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const clearFile = () => {
    setSelectedFile(null);
    setFileDuration(null);
    setDurationError(null);
  };

  // ─── Upload ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!selectedFile || durationError) return;

    try {
      await uploadVideo({
        video: selectedFile,
        schedule_id: scheduleId,
        exercise_sample_id: exerciseSampleId,
        exercise_type: exerciseType,
      }).unwrap();

      setView("processing");
      startPolling();
    } catch {
      setView("error");
    }
  };

  // ─── Retry ────────────────────────────────────────────────────────────────

  const handleRetry = () => {
    stopPolling();
    setSelectedFile(null);
    setFileDuration(null);
    setDurationError(null);
    setView("upload");
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  const isFileValid = selectedFile !== null && !durationError && !validationLoading;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.header}>
          <p className={styles.title}>
            {view === "upload" && "Анализ техники"}
            {view === "processing" && "Анализ техники"}
            {view === "result" && "Результат анализа"}
            {view === "error" && "Ошибка анализа"}
          </p>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Закрыть">
            <X size={16} color="#8e8e93" />
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>

          {/* ── Upload view ── */}
          {view === "upload" && (
            <>
              <div className={styles.requirements}>
                <p className={styles.requirementsTitle}>
                  <Camera size={14} color="#01bb16" />
                  Требования к видео
                </p>
                <ul className={styles.requirementsList}>
                  <li className={styles.requirementsItem}>Только выполнение упражнения</li>
                  <li className={styles.requirementsItem}>Максимум 30 секунд</li>
                  <li className={styles.requirementsItem}>Формат MP4, MOV, AVI</li>
                  <li className={styles.requirementsItem}>Хорошее освещение, полное тело в кадре</li>
                </ul>
              </div>

              {/* Drop zone / file picker */}
              {!selectedFile ? (
                <div
                  className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <div className={styles.dropZoneIcon}>
                    <FileVideo size={36} />
                  </div>
                  <p className={styles.dropZoneText}>Выбрать видео</p>
                  <p className={styles.dropZoneSubtext}>
                    Нажмите или перетащите файл сюда
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/mp4,video/quicktime,video/x-msvideo,.mp4,.mov,.avi"
                    style={{ display: "none" }}
                    onChange={handleFileInputChange}
                  />
                </div>
              ) : (
                <div className={styles.filePreview}>
                  <div className={styles.fileIcon}>
                    <FileVideo size={20} />
                  </div>
                  <div className={styles.fileInfo}>
                    <p className={styles.fileName}>{selectedFile.name}</p>
                    <p className={styles.fileMeta}>
                      {formatBytes(selectedFile.size)}
                      {fileDuration !== null && ` • ${formatDuration(fileDuration)}`}
                      {validationLoading && " • Проверка..."}
                    </p>
                  </div>
                  <button className={styles.fileRemoveBtn} onClick={clearFile} aria-label="Удалить файл">
                    <X size={16} />
                  </button>
                </div>
              )}

              {/* Validation error */}
              {durationError && (
                <div className={styles.errorMessage}>
                  <XCircle size={16} />
                  {durationError}
                </div>
              )}

              {/* Upload indicator */}
              {isUploading && (
                <div className={styles.uploadingIndicator}>
                  <span className={styles.spinner} />
                  Загружаем видео на сервер...
                </div>
              )}
            </>
          )}

          {/* ── Processing view ── */}
          {view === "processing" && (
            <div className={styles.processingState}>
              <div className={styles.pulseRing}>
                <div className={styles.pulseIcon}>
                  <Camera size={24} />
                </div>
              </div>
              <p className={styles.processingTitle}>Анализируем видео...</p>
              <div className={styles.progressDots}>
                <span className={styles.progressDot} />
                <span className={styles.progressDot} />
                <span className={styles.progressDot} />
              </div>
              <p className={styles.processingHint}>
                Это может занять несколько минут. Вы можете закрыть окно и вернуться позже — результат сохранится.
              </p>
            </div>
          )}

          {/* ── Result view ── */}
          {view === "result" && analysisResult && (
            <div className={styles.resultState}>
              <div className={styles.resultHeader}>
                <div className={styles.resultHeaderIcon}>
                  <CheckCircle size={18} />
                </div>
                <div className={styles.resultHeaderText}>
                  <p className={styles.resultHeaderTitle}>Анализ завершён</p>
                  <p className={styles.resultHeaderSub}>Нейросеть обработала ваше видео</p>
                </div>
              </div>

              {analysisResult.result_video_url && (
                <div className={styles.resultVideoWrap}>
                  <video
                    src={analysisResult.result_video_url}
                    controls
                    playsInline
                    preload="metadata"
                  />
                </div>
              )}

              {analysisResult.result_json && (
                <ResultJsonView data={analysisResult.result_json} />
              )}
            </div>
          )}

          {/* ── Error view ── */}
          {view === "error" && (
            <div className={styles.errorState}>
              <div className={styles.errorStateIcon}>
                <XCircle size={32} />
              </div>
              <p className={styles.errorStateTitle}>Не удалось обработать</p>
              <p className={styles.errorStateText}>
                Во время анализа произошла ошибка. Попробуйте загрузить видео ещё раз или проверьте качество записи.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          {view === "upload" && (
            <>
              <button className={styles.btnSecondary} onClick={onClose}>
                Отмена
              </button>
              <button
                className={styles.btnPrimary}
                onClick={handleSubmit}
                disabled={!isFileValid || isUploading}
              >
                {isUploading ? "Загружаем..." : "Отправить на анализ"}
              </button>
            </>
          )}

          {view === "processing" && (
            <button className={styles.btnSecondary} onClick={onClose}>
              Закрыть
            </button>
          )}

          {view === "result" && (
            <button className={styles.btnSecondary} onClick={onClose}>
              Закрыть
            </button>
          )}

          {view === "error" && (
            <>
              <button className={styles.btnSecondary} onClick={onClose}>
                Закрыть
              </button>
              <button className={styles.btnDanger} onClick={handleRetry}>
                Попробовать снова
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
